type ReturnToKind =
  | 'admin'
  | 'topic'
  | 'workspace-settings'
  | 'resource'
  | 'workspace-recipes'

export type ValidatedReturnTo =
  | {
      kind: 'admin'
      href: string
      pathname: '/admin'
      search: string
    }
  | {
      kind: 'topic'
      href: string
      pathname: string
      search: string
      topicId: string
    }
  | {
      kind: 'workspace-settings'
      href: string
      pathname: string
      search: string
      workspaceId: string
    }
  | {
      /** A resource detail page (`/r/:id`). */
      kind: 'resource'
      href: string
      pathname: string
      search: string
      resourceId: string
    }
  | {
      /** The workspace's Recipes page, e.g. with a recipe's setup open. */
      kind: 'workspace-recipes'
      href: string
      pathname: string
      search: string
      workspaceId: string
    }

export interface ReturnToOptions {
  /** Defaults to the current browser origin; injectable for unit tests. */
  baseOrigin?: string
  /** When provided, topic return targets must name this exact topic. */
  expectedTopicId?: string
  /** Narrow the accepted route family for a specific caller. */
  allowedKinds?: readonly ReturnToKind[]
}

function decodedSegment(value: string): string | null {
  try {
    const decoded = decodeURIComponent(value)
    return decoded && !/[\\/?#]/.test(decoded) ? decoded : null
  } catch {
    return null
  }
}

function hasControlCharacter(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index)
    if (code <= 0x1f || code === 0x7f) return true
  }
  return false
}

function topicRoute(pathname: string): { topicId: string } | null {
  const rawParts = pathname.split('/').slice(1)
  if (rawParts[0] !== 'topics') return null
  const topicId = decodedSegment(rawParts[1] ?? '')
  if (!topicId || !/^[1-9]\d*$/.test(topicId)) return null

  const suffix = rawParts.slice(2)
  const allowed =
    suffix.length === 0 ||
    (suffix.length === 1 &&
      (suffix[0] === 'settings' || suffix[0] === 'resources')) ||
    (suffix.length === 2 && suffix[0] === 'resources' && suffix[1] === 'new') ||
    (suffix.length === 3 &&
      suffix[0] === 'resources' &&
      decodedSegment(suffix[1]) !== null &&
      suffix[2] === 'edit')

  return allowed ? { topicId } : null
}

/** `/workspaces/:id/<page>`, exactly. */
function workspacePageRoute(
  pathname: string,
  page: string,
): { workspaceId: string } | null {
  const parts = pathname.split('/').slice(1)
  if (parts[0] !== 'workspaces') return null
  const workspaceId = decodedSegment(parts[1] ?? '')
  if (!workspaceId) return null
  if (parts[2] !== page || parts.length !== 3) return null
  return { workspaceId }
}

function resourceRoute(pathname: string): { resourceId: string } | null {
  const parts = pathname.split('/').slice(1)
  if (parts[0] !== 'r' || parts.length !== 2) return null
  const resourceId = decodedSegment(parts[1] ?? '')
  return resourceId ? { resourceId } : null
}

/**
 * Validate a serialized return location used by nested agent forms.
 *
 * Only current application origins that can actually launch agent creation
 * are accepted: Admin and existing `/topics/:id/*` routes. Absolute URLs,
 * protocol-relative references, backslashes, fragments, malformed escapes,
 * dot-segment normalization, and unregistered paths are rejected.
 */
export function parseReturnTo(
  raw: string | null | undefined,
  opts: ReturnToOptions = {},
): ValidatedReturnTo | null {
  if (!raw || raw[0] !== '/' || raw[1] === '/') return null
  if (raw.includes('\\') || raw.includes('#') || hasControlCharacter(raw)) {
    return null
  }

  try {
    decodeURIComponent(raw)
  } catch {
    return null
  }

  const rawPath = raw.split('?')[0]
  const rawSegments = rawPath.split('/').slice(1)
  if (
    rawSegments.some((segment) => {
      const decoded = decodedSegment(segment)
      return decoded === '.' || decoded === '..'
    })
  ) {
    return null
  }

  const baseOrigin =
    opts.baseOrigin ??
    (typeof globalThis.location === 'undefined'
      ? 'http://localhost'
      : globalThis.location.origin)

  let url: URL
  try {
    const base = new URL(baseOrigin)
    url = new URL(raw, base)
    if (url.origin !== base.origin || url.username || url.password) return null
  } catch {
    return null
  }

  const allowedKinds =
    opts.allowedKinds ??
    ([
      'admin',
      'topic',
      'workspace-settings',
      'resource',
      'workspace-recipes',
    ] as const)
  const href = `${url.pathname}${url.search}`

  if (url.pathname === '/admin' && allowedKinds.includes('admin')) {
    if (opts.expectedTopicId) return null
    return {
      kind: 'admin',
      href,
      pathname: '/admin',
      search: url.search,
    }
  }

  const wsSetting = workspacePageRoute(url.pathname, 'settings')
  if (wsSetting && allowedKinds.includes('workspace-settings')) {
    return {
      kind: 'workspace-settings',
      href,
      pathname: url.pathname,
      search: url.search,
      workspaceId: wsSetting.workspaceId,
    }
  }

  const recipes = workspacePageRoute(url.pathname, 'recipes')
  if (recipes && allowedKinds.includes('workspace-recipes')) {
    if (opts.expectedTopicId) return null
    return {
      kind: 'workspace-recipes',
      href,
      pathname: url.pathname,
      search: url.search,
      workspaceId: recipes.workspaceId,
    }
  }

  const resource = resourceRoute(url.pathname)
  if (resource && allowedKinds.includes('resource')) {
    if (opts.expectedTopicId) return null
    return {
      kind: 'resource',
      href,
      pathname: url.pathname,
      search: url.search,
      resourceId: resource.resourceId,
    }
  }

  const topic = topicRoute(url.pathname)
  if (!topic || !allowedKinds.includes('topic')) return null
  if (opts.expectedTopicId && topic.topicId !== opts.expectedTopicId)
    return null
  return {
    kind: 'topic',
    href,
    pathname: url.pathname,
    search: url.search,
    topicId: topic.topicId,
  }
}

/** Convenience for navigation APIs that only need the sanitized path. */
export function validateReturnTo(
  raw: string | null | undefined,
  opts?: ReturnToOptions,
): string | null {
  return parseReturnTo(raw, opts)?.href ?? null
}

/** Attach a validated return target to an application path. Invalid targets
 * are omitted rather than serialized for a later caller to trust by mistake. */
export function withValidatedReturnTo(
  path: string,
  returnTo: string | null | undefined,
  opts?: ReturnToOptions,
): string {
  const validated = validateReturnTo(returnTo, opts)
  if (!validated) return path
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}return_to=${encodeURIComponent(validated)}`
}
